import type { RequiredDocumentDto, RequiredDocumentsState, RequiredPeriodDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { api, apiDownload, apiUpload, errorMessage } from '../../lib/api';
import { dateOnly, dateTime, humanize } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Dropdown, ErrorText, MenuItem, PageTitle, Select } from '../../ui';
import { useGeneralUpload, WorkingCard } from './ArchiveUpload';
import { useSites } from './SiteScope';

/*
 * Archivio come un Drive (ADR 0122): le cartelle sono le voci dei documenti obbligatori che il sistema ricava per
 * l'azienda; una voce per periodo si apre in anni e, sotto l'anno, nei periodi; una raccolta negli elementi. I file si
 * caricano trascinandoli sulla cartella o dentro la cartella aperta; un documento già in archivio si trascina su una
 * cartella per metterlo lì.
 */

type Voce = RequiredDocumentDto;
type FileDto = Voce['file'][number];
type Period = RequiredPeriodDto;
type Doc = { id: string; titolo: string; tipo?: string | null; caricato_il: string; righe?: number | null; tipo_importato?: string | null; estratto?: string };
type Tone = 'ok' | 'partial' | 'missing' | 'renew' | 'off' | 'neutral';
/** Dove va un file: la voce e, se è una sottocartella, il periodo o l'elemento. */
type Target = { voce: Voce; key: string | null; label: string };

const TONE: Record<Tone, { icon: string; chip: string; dot: string; tile: string; bar: string }> = {
  ok: { icon: 'text-emerald-500', chip: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500', tile: 'border-black/[0.07] bg-white', bar: 'bg-emerald-500' },
  partial: { icon: 'text-amber-500', chip: 'bg-amber-50 text-amber-800', dot: 'bg-amber-500', tile: 'border-black/[0.07] bg-white', bar: 'bg-amber-500' },
  missing: { icon: 'text-amber-400', chip: 'bg-amber-50 text-amber-800', dot: 'bg-amber-400', tile: 'border-dashed border-amber-300 bg-amber-50/40', bar: 'bg-amber-400' },
  renew: { icon: 'text-red-500', chip: 'bg-red-50 text-red-700', dot: 'bg-red-500', tile: 'border-red-200 bg-white', bar: 'bg-red-500' },
  off: { icon: 'text-ink/25', chip: 'bg-black/[0.04] text-ink/50', dot: 'bg-ink/20', tile: 'border-black/[0.06] bg-black/[0.015] opacity-70', bar: 'bg-ink/20' },
  neutral: { icon: 'text-accent/70', chip: 'bg-black/[0.04] text-ink/60', dot: 'bg-ink/25', tile: 'border-black/[0.07] bg-white', bar: 'bg-accent/60' },
};
const VOCE: Record<Voce['stato'], { label: string; tone: Tone }> = {
  da_rinnovare: { label: 'Da rinnovare', tone: 'renew' },
  incompleto: { label: 'Incompleta', tone: 'partial' },
  da_confermare: { label: 'Sono tutti?', tone: 'partial' },
  da_caricare: { label: 'Mancante', tone: 'missing' },
  caricato: { label: 'Completa', tone: 'ok' },
  non_ancora: { label: 'Non ancora dovuta', tone: 'neutral' },
  non_riguarda: { label: 'Non ci riguarda', tone: 'off' },
};
const ORDER: Voce['stato'][] = ['da_rinnovare', 'incompleto', 'da_caricare', 'da_confermare', 'caricato', 'non_ancora', 'non_riguarda'];
const PERIOD_TONE: Record<Period['stato'], Tone> = { caricato: 'ok', mancante: 'missing', non_dovuto: 'off' };
const PERIOD_LABEL: Record<Period['stato'], string> = { caricato: 'Caricato', mancante: 'Manca', non_dovuto: 'Non dovuto' };
/** Le regole di una scheda dei tipi di documento (ADR 0144). */
const RULE_LABEL: Record<'tenuta' | 'termine' | 'validita' | 'conservazione', string> = {
  tenuta: 'come si tiene',
  termine: 'termine',
  validita: 'validità',
  conservazione: 'conservazione',
};
const EVERY: Record<number, string> = { 12: 'uno per ogni anno', 6: 'uno per semestre', 4: 'uno per quadrimestre', 3: 'uno per trimestre', 2: 'uno per bimestre', 1: 'uno per mese' };
const SUBPERIODS: Record<number, string> = { 6: 'semestri', 4: 'quadrimestri', 3: 'trimestri', 2: 'bimestri', 1: 'mesi' };
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const TODO_SHOWN = 6;
const DOC_MIME = 'application/x-bk-doc';
const VIEW_KEY = 'bk-archivio-vista';

const day = (d: string | null) => (d ? dateOnly(d) : '');
const structured = (d: Voce) => d.raccolta || !!d.frequenza_mesi;
const itemsOf = (d: Voce): Period[] => (d.raccolta ? d.elementi : d.periodi) ?? [];
const keyOf = (d: Voce, f: FileDto) => (d.raccolta ? f.elemento : f.periodo);
const labelOf = (d: Voce, f: FileDto) => (d.raccolta ? f.elemento_etichetta : f.periodo_etichetta);
const subPath = (d: Voce) => (d.raccolta ? 'item' : 'period');
const subParam = (d: Voce) => (d.raccolta ? 'elemento' : 'periodo');

/** Periodi o elementi della voce, più quelli che hanno un file ma non sono tra i dovuti (es. il periodo in corso). */
function allItems(d: Voce): Period[] {
  const items = itemsOf(d);
  const known = new Set(items.map((p) => p.chiave));
  const extra = new Map<string, Period>();
  for (const f of d.file) {
    const k = keyOf(d, f);
    if (k && !known.has(k)) extra.set(k, { chiave: k, etichetta: labelOf(d, f) ?? k, stato: 'caricato' });
  }
  return [...items, ...extra.values()].sort((a, b) => (d.raccolta ? 0 : a.chiave.localeCompare(b.chiave)));
}

/** Stato di un insieme di periodi: completo, in parte, mancante, nessuno dovuto. */
function aggregate(items: Period[]): { tone: Tone; done: number; due: number } {
  const due = items.filter((p) => p.stato !== 'non_dovuto');
  const done = due.filter((p) => p.stato === 'caricato').length;
  if (!items.length) return { tone: 'neutral', done: 0, due: 0 };
  if (!due.length) return { tone: 'off', done: 0, due: 0 };
  return { tone: done === due.length ? 'ok' : done === 0 ? 'missing' : 'partial', done, due: due.length };
}

function progress(d: Voce): { done: number; due: number } {
  if (structured(d)) return aggregate(itemsOf(d));
  return { done: d.file.length && d.stato !== 'da_rinnovare' ? 1 : 0, due: d.stato === 'non_ancora' ? 0 : 1 };
}

type Year = { year: string; items: Period[]; files: FileDto[]; tone: Tone; done: number; due: number };
/** Anni di una voce per periodo, dal più recente: ogni anno con i suoi periodi e i suoi file. */
function yearsOf(d: Voce): Year[] {
  const map = new Map<string, Year>();
  const get = (y: string) => {
    if (!map.has(y)) map.set(y, { year: y, items: [], files: [], tone: 'neutral', done: 0, due: 0 });
    return map.get(y)!;
  };
  for (const p of allItems(d)) get(p.chiave.slice(0, 4)).items.push(p);
  for (const f of d.file) if (f.periodo) get(f.periodo.slice(0, 4)).files.push(f);
  return [...map.values()].map((y) => ({ ...y, ...aggregate(y.items) })).sort((a, b) => b.year.localeCompare(a.year));
}

/** Documenti mancanti, uno per riga: prima quelli da rinnovare, poi i periodi più recenti. */
function todos(list: Voce[]): Array<Target & { why: string; tone: Tone }> {
  const out: Array<Target & { why: string; tone: Tone; order: string }> = [];
  for (const d of list) {
    if (d.stato === 'non_riguarda' || d.stato === 'non_ancora') continue;
    if (structured(d)) {
      for (const p of itemsOf(d)) if (p.stato === 'mancante') out.push({ voce: d, key: p.chiave, label: p.etichetta, why: d.raccolta ? (p.fonte ? `risulta da ${p.fonte}` : 'elemento noto') : 'periodo dovuto', tone: 'missing', order: `1${d.raccolta ? '0000' : p.chiave}` });
    } else if (d.stato === 'da_rinnovare') out.push({ voce: d, key: null, label: '', why: d.scade_il ? `scaduto il ${day(d.scade_il)}` : 'scaduto', tone: 'renew', order: '29999' });
    else if (d.stato === 'da_caricare') out.push({ voce: d, key: null, label: '', why: 'non ancora caricato', tone: 'missing', order: '19999' });
  }
  return out.sort((a, b) => b.order.localeCompare(a.order));
}

/* ---------- Contesto della pagina: stato, caricamenti, spostamenti ---------- */

type Drive = {
  base: string;
  state: RequiredDocumentsState | undefined;
  docs: Doc[];
  isManager: boolean;
  view: 'griglia' | 'elenco';
  busy: string | null;
  open: (...path: string[]) => void;
  uploadTo: (to: Target, files: File[]) => void;
  pick: (to: Target) => void;
  /** Un documento già in archivio va nella cartella: da un'altra voce (spostato) o da nessuna. */
  moveTo: (to: Target, docId: string, from: Voce | null) => void;
  act: (voce: Voce, path: string, body?: unknown) => void;
  remove: (doc: { id: string; titolo: string }) => void;
  download: (doc: { id: string; titolo: string }) => void;
  askMove: (doc: { id: string; titolo: string }, from: Voce | null) => void;
};
const DriveCtx = createContext<Drive | null>(null);
const useDrive = () => useContext(DriveCtx)!;

/** Una cartella accetta file dal computer e documenti trascinati dall'archivio. */
function useDropTarget(onFiles: ((files: File[]) => void) | null, onDoc: ((id: string, from: string | null) => void) | null) {
  const [over, setOver] = useState(false);
  const accepts = (e: DragEvent) => (onFiles && e.dataTransfer.types.includes('Files')) || (onDoc && e.dataTransfer.types.includes(DOC_MIME));
  return {
    over,
    props: {
      onDragOver: (e: DragEvent) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = e.dataTransfer.types.includes('Files') ? 'copy' : 'move';
        setOver(true);
      },
      onDragLeave: (e: DragEvent) => {
        if (!(e.currentTarget as Node).contains(e.relatedTarget as Node | null)) setOver(false);
      },
      onDrop: (e: DragEvent) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        const doc = e.dataTransfer.getData(DOC_MIME);
        if (doc && onDoc) {
          const { id, from } = JSON.parse(doc) as { id: string; from: string | null };
          onDoc(id, from);
        } else if (e.dataTransfer.files.length && onFiles) onFiles([...e.dataTransfer.files]);
      },
    },
  };
}

/* ---------- Icone ---------- */

function FolderIcon({ tone, className = 'h-9 w-9' }: { tone: Tone; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`${className} shrink-0 ${TONE[tone].icon}`} aria-hidden>
      <path fill="currentColor" d="M3 6.5A2.5 2.5 0 0 1 5.5 4h3.6c.6 0 1.2.25 1.6.7l1.3 1.3h6.5A2.5 2.5 0 0 1 21 8.5v9a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5v-11Z" opacity={tone === 'missing' ? 0.45 : 1} />
      {tone === 'ok' && <path d="m9 13.5 2 2 4-4" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
      {(tone === 'missing' || tone === 'partial') && <path d="M12 10.5v3.2M12 16h.01" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" />}
      {tone === 'renew' && <path d="M9.5 13a2.5 2.5 0 1 0 .8-1.8M9.5 10.5v1.8h1.8" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

const FILE_KIND: Array<[RegExp, string, string]> = [
  [/\.pdf$|\.p7m$/i, 'PDF', 'bg-red-500'],
  [/\.(xlsx?|csv|tsv|ods)$/i, 'XLS', 'bg-emerald-600'],
  [/\.(docx?|odt|rtf)$/i, 'DOC', 'bg-blue-600'],
  [/\.(png|jpe?g|gif|webp|heic|tiff?)$/i, 'IMG', 'bg-violet-500'],
  [/\.(zip|rar|7z|tar|gz)$/i, 'ZIP', 'bg-stone-500'],
  [/\.(eml|msg)$/i, 'MAIL', 'bg-sky-500'],
  [/\.xml$/i, 'XML', 'bg-orange-500'],
];
function FileIcon({ name, big = false }: { name: string; big?: boolean }) {
  const [, label, color] = FILE_KIND.find(([re]) => re.test(name)) ?? [null, 'FILE', 'bg-ink/40'];
  return (
    <span className={`relative inline-flex shrink-0 items-end justify-center rounded-[4px] ${color} font-bold text-white ${big ? 'h-14 w-11 pb-1.5 text-[10px]' : 'h-7 w-[22px] pb-0.5 text-[7px]'}`} aria-hidden>
      <span className={`absolute right-0 top-0 rounded-bl-[3px] bg-white/35 ${big ? 'h-3 w-3' : 'h-1.5 w-1.5'}`} />
      {label}
    </span>
  );
}

function Chip({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`inline-flex items-center gap-1 shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE[tone].chip}`}>{children}</span>;
}

function Bar({ done, due, tone }: { done: number; due: number; tone: Tone }) {
  if (!due) return null;
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-black/[0.06]">
      <div className={`h-full rounded-full ${TONE[tone].bar}`} style={{ width: `${Math.round((done / due) * 100)}%` }} />
    </div>
  );
}

/* ---------- Cartelle ---------- */

function FolderTile({
  title,
  subtitle,
  tone,
  badge,
  done = 0,
  due = 0,
  onOpen,
  drop,
  ghost,
}: {
  title: string;
  subtitle?: ReactNode;
  tone: Tone;
  badge?: ReactNode;
  done?: number;
  due?: number;
  onOpen: () => void;
  drop?: ReturnType<typeof useDropTarget>;
  /** Cartella che manca: il clic carica invece di aprire. */
  ghost?: string;
}) {
  const { view } = useDrive();
  const over = drop?.over;
  const ring = over ? 'ring-2 ring-accent bg-accent/5' : 'hover:border-accent/40 hover:shadow-[0_4px_16px_rgba(8,21,66,0.08)]';
  if (view === 'elenco')
    return (
      <button type="button" onClick={onOpen} {...drop?.props} className={`flex w-full items-center gap-3 border-b border-black/[0.05] px-3 py-2 text-left text-sm transition first:rounded-t-xl last:rounded-b-xl last:border-0 ${over ? 'bg-accent/5 ring-2 ring-inset ring-accent' : 'hover:bg-accent/[0.04]'}`}>
        <FolderIcon tone={tone} className="h-6 w-6" />
        <span className="min-w-0 flex-1 truncate font-medium">{title}</span>
        {subtitle && <span className="hidden min-w-0 max-w-[40%] truncate text-xs text-ink/50 sm:block">{subtitle}</span>}
        {due > 0 && <span className="hidden w-20 sm:block"><Bar done={done} due={due} tone={tone} /></span>}
        {badge}
        {ghost && <span className="text-xs font-medium text-accent">{ghost}</span>}
      </button>
    );
  return (
    <button type="button" onClick={onOpen} {...drop?.props} className={`group flex min-h-[5.5rem] flex-col gap-2 rounded-xl border p-3 text-left transition ${TONE[tone].tile} ${ring}`}>
      <div className="flex w-full items-start gap-2.5">
        <FolderIcon tone={tone} />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium leading-snug">{title}</p>
          {subtitle && <p className="mt-0.5 truncate text-xs text-ink/50">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-auto flex w-full items-center gap-2">
        {due > 0 ? <Bar done={done} due={due} tone={tone} /> : <span className="flex-1" />}
        {badge}
      </div>
      {ghost && <span className="text-xs font-medium text-accent opacity-80 group-hover:opacity-100">{ghost}</span>}
    </button>
  );
}

function VoceTile({ d }: { d: Voce }) {
  const drive = useDrive();
  const st = VOCE[d.stato];
  const p = progress(d);
  const to: Target = { voce: d, key: null, label: '' };
  const drop = useDropTarget(drive.isManager && d.stato !== 'non_riguarda' ? (f) => drive.uploadTo(to, f) : null, drive.isManager && d.stato !== 'non_riguarda' ? (id, from) => drive.moveTo(to, id, voceById(drive, from)) : null);
  const sub = d.raccolta ? `${allItems(d).length} elementi` : d.frequenza_mesi ? (EVERY[d.frequenza_mesi] ?? `ogni ${d.frequenza_mesi} mesi`) : d.validita_mesi ? `da rinnovare ogni ${d.validita_mesi} mesi` : d.file[0] ? d.file[0].titolo : 'documento unico';
  return (
    <FolderTile
      title={d.titolo}
      subtitle={sub}
      tone={st.tone}
      done={p.done}
      due={structured(d) ? p.due : 0}
      onOpen={() => drive.open(d.id)}
      drop={drop}
      badge={<Chip tone={st.tone}>{d.mancanti > 0 && structured(d) ? `mancano ${d.mancanti}` : st.label}</Chip>}
    />
  );
}

const voceById = (drive: Drive, id: string | null) => (id ? (drive.state?.data.find((d) => d.id === id) ?? null) : null);

/** Sottocartella di una voce (un anno, un periodo, un elemento). */
function SubTile({ voce, title, subtitle, tone, done, due, target, path, missing }: { voce: Voce; title: string; subtitle?: string; tone: Tone; done?: number; due?: number; target: Target; path: string[]; missing?: boolean }) {
  const drive = useDrive();
  const can = drive.isManager && voce.stato !== 'non_riguarda';
  const drop = useDropTarget(can ? (f) => drive.uploadTo(target, f) : null, can ? (id, from) => drive.moveTo(target, id, voceById(drive, from)) : null);
  return (
    <FolderTile
      title={title}
      subtitle={subtitle}
      tone={tone}
      done={done}
      due={due}
      drop={drop}
      onOpen={() => drive.open(voce.id, ...path)}
      badge={<Chip tone={tone}>{missing ? 'Manca' : tone === 'ok' ? 'Completo' : tone === 'off' ? 'Non dovuto' : tone === 'partial' ? `${done} di ${due}` : tone === 'missing' ? 'Manca' : 'Aperto'}</Chip>}
    />
  );
}

/* ---------- File ---------- */

function FileItem({ doc, voce, sub, extra }: { doc: { id: string; titolo: string }; voce: Voce | null; sub?: ReactNode; extra?: ReactNode }) {
  const drive = useDrive();
  const drag = drive.isManager
    ? {
        draggable: true,
        onDragStart: (e: DragEvent) => {
          e.dataTransfer.setData(DOC_MIME, JSON.stringify({ id: doc.id, from: voce?.id ?? null }));
          e.dataTransfer.effectAllowed = 'move';
        },
      }
    : {};
  const menu = (
    <Dropdown label={`Azioni su ${doc.titolo}`} align="right" trigger={<span className="text-ink/50">⋯</span>}>
      {(close) => (
        <>
          <MenuItem onClick={() => (close(), drive.download(doc))}>Scarica</MenuItem>
          {drive.isManager && <MenuItem onClick={() => (close(), drive.askMove(doc, voce))}>{voce ? 'Sposta in un’altra cartella…' : 'Metti in una cartella…'}</MenuItem>}
          {drive.isManager && voce && <MenuItem onClick={() => (close(), drive.act(voce, `files/${doc.id}/unlink`))}>Togli dalla cartella (resta in archivio)</MenuItem>}
          {drive.isManager && (
            <MenuItem tone="danger" onClick={() => (close(), drive.remove(doc))}>
              Elimina definitivamente
            </MenuItem>
          )}
        </>
      )}
    </Dropdown>
  );
  if (drive.view === 'griglia')
    return (
      <div {...drag} className="group relative flex flex-col items-center gap-2 rounded-xl border border-black/[0.07] bg-white p-3 text-center transition hover:border-accent/40 hover:shadow-[0_4px_16px_rgba(8,21,66,0.08)]">
        <button type="button" className="flex w-full flex-col items-center gap-2" onClick={() => drive.download(doc)} title="Scarica">
          <FileIcon name={doc.titolo} big />
          <span className="line-clamp-2 w-full break-words text-xs font-medium">{doc.titolo}</span>
        </button>
        {sub && <span className="w-full truncate text-[11px] text-ink/50">{sub}</span>}
        {extra}
        <div className="absolute right-1 top-1">{menu}</div>
      </div>
    );
  return (
    <div {...drag} className="flex items-center gap-3 border-b border-black/[0.05] px-3 py-2 text-sm first:rounded-t-xl last:rounded-b-xl last:border-0 hover:bg-accent/[0.04]">
      <FileIcon name={doc.titolo} />
      <button type="button" className="min-w-0 flex-1 truncate text-left hover:text-accent" onClick={() => drive.download(doc)} title="Scarica">
        {doc.titolo}
      </button>
      {sub && <span className="hidden max-w-[40%] truncate text-xs text-ink/50 sm:block">{sub}</span>}
      {extra}
      {menu}
    </div>
  );
}

/** `files` = riquadri dei file; `dense` = sottocartelle (anni, periodi, elementi), più piccole delle voci. */
function Grid({ children, files = false, dense = false }: { children: ReactNode; files?: boolean; dense?: boolean }) {
  const { view } = useDrive();
  const cols = files ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6' : dense ? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3';
  return view === 'griglia' ? (
    <div className={`grid gap-3 ${cols}`}>{children}</div>
  ) : (
    // Niente overflow-hidden: taglierebbe il menu delle azioni sui file quando le righe sono poche.
    <div className="rounded-xl border border-black/[0.07] bg-white">{children}</div>
  );
}

function Section({ title, children, right }: { title: ReactNode; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="flex-1 text-xs font-semibold uppercase tracking-wide text-ink/45">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

/** Cartella vuota: il riquadro dove trascinare il file che manca. */
function EmptyDrop({ target, text, hint }: { target: Target | null; text: string; hint?: string | null }) {
  const drive = useDrive();
  const can = drive.isManager && !!target;
  const drop = useDropTarget(can ? (f) => drive.uploadTo(target!, f) : null, can ? (id, from) => drive.moveTo(target!, id, voceById(drive, from)) : null);
  return (
    <button
      type="button"
      disabled={!can}
      onClick={() => target && drive.pick(target)}
      {...drop.props}
      className={`flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 text-center text-sm transition ${drop.over ? 'border-accent bg-accent/5' : 'border-amber-300 bg-amber-50/30 hover:border-accent/60'} disabled:cursor-default`}
    >
      <FolderIcon tone="missing" className="h-12 w-12" />
      <span className="font-medium text-ink/80">{text}</span>
      {hint && <span className="max-w-lg text-xs text-ink/55">{hint}</span>}
      {can && <span className="text-xs font-medium text-accent">Trascina qui il file o fai clic per sceglierlo</span>}
    </button>
  );
}

/* ---------- Viste ---------- */

function RootView({ list, filter, setFilter, docs, unlinked }: { list: Voce[]; filter: string; setFilter: (f: string) => void; docs: Doc[]; unlinked: Doc[] }) {
  const drive = useDrive();
  const sites = useSites();
  const relevant = list.filter((d) => d.stato !== 'non_riguarda');
  const complete = relevant.filter((d) => d.stato === 'caricato' || d.stato === 'non_ancora').length;
  const tasks = todos(list);
  const renew = list.filter((d) => d.stato === 'da_rinnovare').length;
  const [allTodo, setAllTodo] = useState(false);
  const FILTERS: Array<[string, string, (d: Voce) => boolean]> = [
    ['tutte', `Tutte (${relevant.length})`, (d) => d.stato !== 'non_riguarda'],
    ['da_sistemare', `Da sistemare (${relevant.length - complete})`, (d) => ['da_rinnovare', 'incompleto', 'da_caricare', 'da_confermare'].includes(d.stato)],
    ['complete', `Complete (${complete})`, (d) => d.stato === 'caricato' || d.stato === 'non_ancora'],
    ['escluse', `Non ci riguardano (${list.length - relevant.length})`, (d) => d.stato === 'non_riguarda'],
  ];
  const shown = list.filter((FILTERS.find((f) => f[0] === filter) ?? FILTERS[0]!)[2]).sort((a, b) => ORDER.indexOf(a.stato) - ORDER.indexOf(b.stato) || a.titolo.localeCompare(b.titolo));
  const groups = groupBySite(shown, sites.multi, sites.name);
  const pct = relevant.length ? Math.round((complete / relevant.length) * 100) : 0;
  return (
    <div className="space-y-7">
      {relevant.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-black/[0.07] bg-white p-4">
          <div className="relative h-16 w-16 shrink-0">
            <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3.5" className="text-black/[0.06]" />
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" className={pct === 100 ? 'text-emerald-500' : 'text-accent'} strokeDasharray={`${(pct / 100) * 97.4} 97.4`} />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold">{pct}%</span>
          </div>
          <div className="min-w-[12rem] flex-1">
            <p className="text-sm font-semibold">
              {complete} di {relevant.length} cartelle complete
            </p>
            <p className="text-xs text-ink/55">Le cartelle sono i documenti che il sistema ha ricavato per questa azienda.</p>
          </div>
          <Stat n={tasks.filter((x) => x.tone === 'missing').length} label="documenti mancanti" tone="missing" />
          <Stat n={renew} label="da rinnovare" tone="renew" />
          <Stat n={unlinked.length} label="altri documenti" tone="neutral" />
        </div>
      )}

      <WorkingCard />

      {tasks.length > 0 && (
        <Section title={`Da fare · ${tasks.length}`} right={tasks.length > TODO_SHOWN && <button className="text-xs text-accent hover:underline" onClick={() => setAllTodo(!allTodo)}>{allTodo ? 'Mostra meno' : `Mostra tutti (${tasks.length})`}</button>}>
          <div className="overflow-hidden rounded-xl border border-black/[0.07] bg-white">
            {(allTodo ? tasks : tasks.slice(0, TODO_SHOWN)).map((x) => (
              <TodoRow key={`${x.voce.id}/${x.key ?? ''}`} task={x} />
            ))}
          </div>
        </Section>
      )}

      {list.length > 0 && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} className={`rounded-full border px-3 py-1 text-xs font-medium transition ${filter === k ? 'border-accent bg-accent text-white' : 'border-black/10 bg-white text-ink/65 hover:border-accent/40'}`}>
                {label}
              </button>
            ))}
          </div>
          {groups.map((g) => (
            <Section key={g.key} title={g.label || 'Cartelle'}>
              <Grid>
                {g.items.map((d) => (
                  <VoceTile key={d.id} d={d} />
                ))}
              </Grid>
            </Section>
          ))}
          {!shown.length && <p className="text-sm text-ink/50">Nessuna cartella con questo filtro.</p>}
        </div>
      )}

      <Section title="Altri documenti">
        <Grid>
          <FolderTile title="Altri documenti" subtitle={`${unlinked.length} non in una cartella · per anno e mese di arrivo`} tone="neutral" onOpen={() => drive.open('altri')} />
        </Grid>
      </Section>

      {docs.length > 0 && (
        <Section title="Caricati di recente">
          <Grid files>
            {docs.slice(0, 8).map((d) => (
              <FileItem key={d.id} doc={d} voce={null} sub={dateTime(d.caricato_il)} />
            ))}
          </Grid>
        </Section>
      )}
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone: Tone }) {
  return (
    <div className="shrink-0">
      <p className={`text-xl font-semibold ${n && tone !== 'neutral' ? TONE[tone].icon : 'text-ink/70'}`}>{n}</p>
      <p className="text-xs text-ink/55">{label}</p>
    </div>
  );
}

function TodoRow({ task }: { task: Target & { why: string; tone: Tone } }) {
  const drive = useDrive();
  const drop = useDropTarget(drive.isManager ? (f) => drive.uploadTo(task, f) : null, drive.isManager ? (id, from) => drive.moveTo(task, id, voceById(drive, from)) : null);
  return (
    <div {...drop.props} className={`flex items-center gap-3 border-b border-black/[0.05] px-3 py-2 text-sm last:border-0 ${drop.over ? 'bg-accent/5 ring-2 ring-inset ring-accent' : ''}`}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${TONE[task.tone].dot}`} />
      <button className="min-w-0 flex-1 truncate text-left hover:text-accent" onClick={() => (task.key ? drive.open(task.voce.id, ...subPathOf(task.voce, task.key)) : drive.open(task.voce.id))}>
        <span className="font-medium">{task.voce.titolo}</span>
        {task.label && <span className="text-ink/70"> · {task.label}</span>}
        <span className="ml-2 text-xs text-ink/45">{task.why}</span>
      </button>
      {drive.isManager && (
        <Button variant="ghost" className="shrink-0 !px-3 !py-1 text-xs text-accent" onClick={() => drive.pick(task)}>
          {task.tone === 'renew' ? 'Carica il nuovo' : 'Carica'}
        </Button>
      )}
    </div>
  );
}

/** Percorso dentro la voce di un periodo o di un elemento. */
function subPathOf(d: Voce, key: string): string[] {
  if (d.raccolta) return [encodeURIComponent(key)];
  return d.frequenza_mesi === 12 ? [key] : [key.slice(0, 4), key];
}

/** Voci per livello: l'azienda come soggetto, poi ogni sede (una voce di una sede ha una sola sede). */
function groupBySite(list: Voce[], multi: boolean, name: (id: string) => string) {
  if (!multi) return [{ key: 'tutti', label: '', items: list }];
  const out = new Map<string, { key: string; label: string; items: Voce[] }>();
  for (const d of list) {
    const site = d.ambito.ambito === 'sedi' ? d.ambito.sedi[0]! : null;
    const key = site ?? 'azienda';
    if (!out.has(key)) out.set(key, { key, label: site ? `Sede ${name(site)}` : 'Azienda', items: [] });
    out.get(key)!.items.push(d);
  }
  return [...out.values()].sort((a, b) => (a.key === 'azienda' ? -1 : b.key === 'azienda' ? 1 : a.label.localeCompare(b.label)));
}

function VoceView({ d, sub }: { d: Voce; sub: string[] }) {
  const drive = useDrive();
  const files = (list: FileDto[], showKey = false) => (
    <Grid files>
      {[...list]
        .sort((a, b) => (b.data ?? '').localeCompare(a.data ?? ''))
        .map((f) => (
          <FileItem key={f.id} doc={f} voce={d} sub={[showKey ? labelOf(d, f) : null, f.data ? `del ${day(f.data)}` : null, f.collegato_da === 'motore' ? 'riconosciuto dal sistema' : null].filter(Boolean).join(' · ')} />
        ))}
    </Grid>
  );
  const root: Target = { voce: d, key: null, label: '' };
  const off = d.stato === 'non_riguarda';

  // Documento unico o da rinnovare: subito i file.
  if (!structured(d))
    return d.file.length ? (
      <div className="space-y-3">
        {d.scade_il && (
          <p className={`rounded-lg px-3 py-2 text-sm ${d.stato === 'da_rinnovare' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'}`}>
            {d.stato === 'da_rinnovare' ? `Scaduto il ${day(d.scade_il)}: carica il documento nuovo.` : `Valido fino al ${day(d.scade_il)}.`}
          </p>
        )}
        {files(d.file)}
      </div>
    ) : (
      <EmptyDrop target={off ? null : root} text={`Carica «${d.titolo}»`} hint={d.cosa_caricare} />
    );

  // Raccolta: una cartella per elemento.
  if (d.raccolta) {
    const items = allItems(d);
    const key = sub[0] ? decodeURIComponent(sub[0]) : null;
    const item = items.find((p) => p.chiave === key);
    if (key) {
      const mine = d.file.filter((f) => f.elemento === key);
      const target: Target = { voce: d, key, label: item?.etichetta ?? key };
      return mine.length ? files(mine) : <EmptyDrop target={off ? null : target} text={`Carica per ${item?.etichetta ?? key}`} hint={item?.fonte ? `Risulta da: ${item.fonte}` : d.cosa_caricare} />;
    }
    const loose = d.file.filter((f) => !f.elemento);
    return (
      <div className="space-y-6">
        {items.length > 0 ? (
          <Grid dense>
            {items.map((p) => (
              <SubTile key={p.chiave} voce={d} title={p.etichetta} subtitle={p.fonte ? `risulta da ${p.fonte}` : undefined} tone={PERIOD_TONE[p.stato]} target={{ voce: d, key: p.chiave, label: p.etichetta }} path={[encodeURIComponent(p.chiave)]} missing={p.stato === 'mancante'} />
            ))}
          </Grid>
        ) : (
          !loose.length && <EmptyDrop target={off ? null : root} text={`Carica i documenti di «${d.titolo}»`} hint={d.cosa_caricare} />
        )}
        {loose.length > 0 && <Section title={`Da assegnare · ${loose.length}`}>{files(loose)}</Section>}
      </div>
    );
  }

  // Per periodo: anni, e sotto l'anno i periodi se sono più brevi dell'anno.
  const months = d.frequenza_mesi!;
  const years = yearsOf(d);
  const loose = d.file.filter((f) => !f.periodo);
  if (sub[0]) {
    const year = years.find((y) => y.year === sub[0]);
    const leaf = months === 12 ? sub[0] : sub[1];
    if (leaf) {
      const p = allItems(d).find((x) => x.chiave === leaf);
      const mine = d.file.filter((f) => f.periodo === leaf);
      const target: Target = { voce: d, key: leaf, label: p?.etichetta ?? leaf };
      return mine.length ? files(mine) : <EmptyDrop target={off ? null : target} text={p?.stato === 'non_dovuto' ? `${p.etichetta}: segnato come non dovuto` : `Manca «${d.titolo}» · ${p?.etichetta ?? leaf}`} hint={p?.stato === 'non_dovuto' ? null : d.cosa_caricare} />;
    }
    if (!year) return <p className="text-sm text-ink/50">Nessun periodo per il {sub[0]}.</p>;
    return (
      <Grid dense>
        {year.items.map((p) => (
          <SubTile key={p.chiave} voce={d} title={p.etichetta} tone={PERIOD_TONE[p.stato]} target={{ voce: d, key: p.chiave, label: p.etichetta }} path={[year.year, p.chiave]} missing={p.stato === 'mancante'} subtitle={fileCount(d, p.chiave)} />
        ))}
      </Grid>
    );
  }
  return (
    <div className="space-y-6">
      {!d.dal && !off && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Non si sa da quando serve: chiedo solo l’ultimo periodo. Indica la data di costituzione nella sezione Azienda e aggiorna l’elenco.</p>}
      {years.length ? (
        <Grid dense>
          {years.map((y) =>
            months === 12 ? (
              <SubTile key={y.year} voce={d} title={y.year} tone={y.tone} target={{ voce: d, key: y.year, label: y.year }} path={[y.year]} missing={y.tone === 'missing'} subtitle={fileCount(d, y.year)} />
            ) : (
              <SubTile key={y.year} voce={d} title={y.year} tone={y.tone} done={y.done} due={y.due} target={{ voce: d, key: null, label: y.year }} path={[y.year]} subtitle={`${y.done} di ${y.due} ${SUBPERIODS[months] ?? 'periodi'}`} />
            ),
          )}
        </Grid>
      ) : (
        !loose.length && <EmptyDrop target={off ? null : root} text={`Nessun periodo ancora dovuto per «${d.titolo}»`} hint={d.cosa_caricare} />
      )}
      {loose.length > 0 && <Section title={`Senza periodo · ${loose.length}`}>{files(loose)}</Section>}
    </div>
  );
}

const fileCount = (d: Voce, key: string) => {
  const n = d.file.filter((f) => f.periodo === key).length;
  return n ? `${n} file` : undefined;
};

function OthersView({ docs, sub }: { docs: Doc[]; sub: string[] }) {
  const drive = useDrive();
  const byYear = new Map<string, Map<string, Doc[]>>();
  for (const d of docs) {
    const [y, m] = [d.caricato_il.slice(0, 4), d.caricato_il.slice(5, 7)];
    if (!byYear.has(y)) byYear.set(y, new Map());
    const months = byYear.get(y)!;
    if (!months.has(m)) months.set(m, []);
    months.get(m)!.push(d);
  }
  const list = (items: Doc[]) => (
    <Grid files>
      {items.map((d) => (
        <FileItem key={d.id} doc={d} voce={null} sub={[dateTime(d.caricato_il), d.righe ? `${d.righe} righe` : null, d.tipo_importato ? `in ${humanize(d.tipo_importato)}` : null].filter(Boolean).join(' · ')} />
      ))}
    </Grid>
  );
  if (!docs.length) return <p className="text-sm text-ink/50">Nessun documento fuori dalle cartelle tra gli ultimi caricati.</p>;
  if (sub[1]) return list(byYear.get(sub[0]!)?.get(sub[1]) ?? []);
  if (sub[0])
    return (
      <Grid>
        {[...(byYear.get(sub[0]) ?? new Map<string, Doc[]>()).entries()]
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([m, items]) => (
            <FolderTile key={m} title={MONTHS[Number(m) - 1] ?? m} subtitle={`${items.length} documenti`} tone="neutral" onOpen={() => drive.open('altri', sub[0]!, m)} />
          ))}
      </Grid>
    );
  return (
    <div className="space-y-6">
      <p className="text-xs text-ink/50">Documenti che non sono in nessuna cartella dei documenti obbligatori (gli ultimi 100 arrivati; per i più vecchi usa la ricerca). Trascinane uno su una cartella dell’albero, o usa «Metti in una cartella».</p>
      <Grid>
        {[...byYear.entries()]
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([y, months]) => (
            <FolderTile key={y} title={y} subtitle={`${[...months.values()].reduce((n, x) => n + x.length, 0)} documenti`} tone="neutral" onOpen={() => drive.open('altri', y)} />
          ))}
      </Grid>
    </div>
  );
}

function SearchView({ q, list }: { q: string; list: Voce[] }) {
  const drive = useDrive();
  const results = useTenantQuery<{ data: Doc[] }>(['archive', 'documents', q], `/archive/documents?q=${encodeURIComponent(q)}`);
  const words = q.toLowerCase();
  const folders = list.filter((d) => d.titolo.toLowerCase().includes(words) || d.file.some((f) => f.titolo.toLowerCase().includes(words)));
  const where = (id: string) => list.find((d) => d.file.some((f) => f.id === id)) ?? null;
  return (
    <div className="space-y-6">
      {folders.length > 0 && (
        <Section title="Cartelle">
          <Grid>
            {folders.map((d) => (
              <VoceTile key={d.id} d={d} />
            ))}
          </Grid>
        </Section>
      )}
      <Section title="Documenti che contengono il testo">
        <ErrorText>{results.error ? errorMessage(results.error) : ''}</ErrorText>
        {results.isPending ? (
          <p className="text-sm text-ink/50">Cerco…</p>
        ) : results.data?.data.length ? (
          <div className="rounded-xl border border-black/[0.07] bg-white">
            {results.data.data.map((d) => {
              const v = where(d.id);
              return (
                <div key={d.id} className="border-b border-black/[0.05] last:border-0">
                  <FileItem doc={d} voce={v} sub={v ? <button className="text-accent hover:underline" onClick={() => drive.open(v.id)}>in {v.titolo}</button> : dateTime(d.caricato_il)} />
                  {d.estratto && <p className="px-3 pb-2 pl-12 text-xs text-ink/60" dangerouslySetInnerHTML={{ __html: d.estratto.replace(/<(?!\/?b>)[^>]*>/g, '') }} />}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-ink/50">Nessun documento contiene «{q}».</p>
        )}
      </Section>
    </div>
  );
}

/* ---------- Pannello dei dettagli ---------- */

function Details({ d, leaf }: { d: Voce; leaf: Period | null }) {
  const drive = useDrive();
  const st = VOCE[d.stato];
  const off = d.stato === 'non_riguarda';
  const p = progress(d);
  return (
    <div className="space-y-3 rounded-xl border border-black/[0.07] bg-white p-4 text-sm">
      <div className="flex items-start gap-2">
        <FolderIcon tone={st.tone} className="h-7 w-7" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-snug">{d.titolo}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Chip tone={st.tone}>{st.label}</Chip>
            {structured(d) && p.due > 0 && (
              <span className="text-xs text-ink/55">
                {p.done} di {p.due} {d.raccolta ? 'elementi' : 'periodi'}
              </span>
            )}
          </div>
        </div>
      </div>
      {leaf && (
        <div className="rounded-lg bg-black/[0.03] p-2.5 text-xs">
          <p className="font-medium">
            {leaf.etichetta}: {PERIOD_LABEL[leaf.stato].toLowerCase()}
          </p>
          {leaf.fonte && <p className="text-ink/55">Risulta da: {leaf.fonte}</p>}
          {drive.isManager && leaf.stato !== 'caricato' && (
            <button className="mt-1 text-accent hover:underline" onClick={() => drive.act(d, d.raccolta ? 'items' : 'periods', { [subParam(d)]: leaf.chiave, non_dovuto: leaf.stato === 'mancante' })}>
              {leaf.stato === 'mancante' ? (d.raccolta ? 'Non serve' : 'Non era dovuto') : d.raccolta ? 'Serve' : 'Era dovuto'}
            </button>
          )}
        </div>
      )}
      {d.cosa_caricare && !off && (
        <Info label="Cosa caricare">{d.cosa_caricare}</Info>
      )}
      <Info label="Perché">{d.perche}</Info>
      {d.riferimento && <Info label="Riferimento">{d.riferimento}</Info>}
      {(d.frequenza_mesi || d.validita_mesi || d.raccolta) && (
        <Info label="Cadenza">
          {d.raccolta
            ? 'un documento per ciascuno'
            : d.frequenza_mesi
              ? `${EVERY[d.frequenza_mesi] ?? `uno ogni ${d.frequenza_mesi} mesi`}${d.dal ? ` dal ${day(d.dal)}` : ''}`
              : `da rinnovare ogni ${d.validita_mesi} mesi`}
        </Info>
      )}
      {d.regole && (
        <>
          <Info label="Formato">{d.regole.formati}</Info>
          <Info label="Non valgono">{d.regole.non_valgono.join('; ')}</Info>
          {d.regole.norma && <Info label="Termine">{d.regole.norma}</Info>}
          {d.regole.conservazione_anni && <Info label="Conservazione">{d.regole.conservazione_anni} anni</Info>}
          {d.regole.da_verificare.length > 0 && (
            <p className="text-xs text-amber-700">
              Regole da verificare (nessuna norma trovata, non si applicano): {d.regole.da_verificare.map((r) => RULE_LABEL[r]).join(', ')}
            </p>
          )}
        </>
      )}
      {d.condizione && d.stato !== 'caricato' && <p className="text-xs text-amber-700">Da verificare: {d.condizione}</p>}
      {d.stato === 'da_confermare' && <p className="text-xs text-amber-700">Ci sono i documenti degli elementi noti, ma il sistema non può sapere se sono tutti: aggiungi quelli che mancano o conferma.</p>}
      {drive.isManager && (
        <div className="flex flex-wrap gap-1.5 border-t border-black/[0.06] pt-3">
          {!off && (
            <Button className="!px-3 !py-1.5 text-xs" onClick={() => drive.pick({ voce: d, key: leaf?.chiave ?? null, label: leaf?.etichetta ?? '' })}>
              {leaf ? `Carica per ${leaf.etichetta}` : d.stato === 'da_rinnovare' ? 'Carica il nuovo' : 'Carica qui'}
            </Button>
          )}
          {d.raccolta && !off && d.file.length > 0 && d.mancanti === 0 && (
            <Button variant="ghost" className="!px-3 !py-1.5 text-xs" onClick={() => drive.act(d, 'confirm', { completo: !d.confermato })}>
              {d.confermato ? 'Non sono tutti' : 'Sono tutti'}
            </Button>
          )}
          <Button variant="ghost" className="!px-3 !py-1.5 text-xs" onClick={() => drive.act(d, 'status', { stato: off ? 'attivo' : 'non_riguarda' })}>
            {off ? 'Ci riguarda' : 'Non ci riguarda'}
          </Button>
        </div>
      )}
    </div>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/40">{label}</p>
      <p className="text-xs text-ink/75">{children}</p>
    </div>
  );
}

function RootDetails() {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const { state } = useDrive();
  const generate = useMutation({
    mutationFn: () => api<RequiredDocumentsState>(t('/archive/required/generate'), { body: {} }),
    onSuccess: (s) => queryClient.setQueryData(['tenant', tenant.id, 'archive', 'required'], s),
  });
  return (
    <div className="space-y-3 rounded-xl border border-black/[0.07] bg-white p-4 text-xs text-ink/65">
      <p className="text-sm font-semibold text-ink">Come funziona</p>
      <p>Le cartelle sono i documenti che {tenant.name} deve avere, ricavati dal sistema da ciò che sa dell’azienda (attività, forma, persone, obblighi).</p>
      <p>Quelle che si fanno per periodo si aprono per anno e poi per semestre, trimestre o mese; le raccolte per soggetto, pratica o bene. Le cartelle tratteggiate sono quelle che mancano.</p>
      <p>Trascina i file su una cartella per caricarli lì; trascina un documento già in archivio su una cartella per spostarlo.</p>
      {state?.generated_at && <p className="text-ink/45">Elenco aggiornato {dateTime(state.generated_at)}.</p>}
      {isManager && state && !state.needs_profile && (
        <Button variant="ghost" className="!px-3 !py-1.5 text-xs" disabled={state.generating || generate.isPending} onClick={() => generate.mutate()}>
          {state.generating ? 'Preparo l’elenco…' : 'Aggiorna l’elenco'}
        </Button>
      )}
      <ErrorText>{generate.error ? errorMessage(generate.error) : (state?.error ?? '')}</ErrorText>
    </div>
  );
}

/* ---------- Albero ---------- */

function Tree({ list, current }: { list: Voce[]; current: string | undefined }) {
  const drive = useDrive();
  const sites = useSites();
  const relevant = list.filter((d) => d.stato !== 'non_riguarda').sort((a, b) => a.titolo.localeCompare(b.titolo));
  return (
    <nav className="space-y-3 text-sm" aria-label="Cartelle dell'archivio">
      <TreeRow label="Archivio" active={!current} onClick={() => drive.open()} icon={<FolderIcon tone="neutral" className="h-5 w-5" />} />
      {groupBySite(relevant, sites.multi, sites.name).map((g) => (
        <div key={g.key}>
          {g.label && <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-ink/40">{g.label}</p>}
          {g.items.map((d) => (
            <TreeVoce key={d.id} d={d} active={current === d.id} />
          ))}
        </div>
      ))}
      <TreeRow label="Altri documenti" active={current === 'altri'} onClick={() => drive.open('altri')} icon={<FolderIcon tone="neutral" className="h-5 w-5" />} />
    </nav>
  );
}

function TreeVoce({ d, active }: { d: Voce; active: boolean }) {
  const drive = useDrive();
  const to: Target = { voce: d, key: null, label: '' };
  const drop = useDropTarget(drive.isManager ? (f) => drive.uploadTo(to, f) : null, drive.isManager ? (id, from) => drive.moveTo(to, id, voceById(drive, from)) : null);
  const tone = VOCE[d.stato].tone;
  return (
    <div {...drop.props} className={drop.over ? 'rounded-lg ring-2 ring-accent' : ''}>
      <TreeRow
        label={d.titolo}
        active={active}
        onClick={() => drive.open(d.id)}
        icon={<span className={`ml-1.5 mr-0.5 h-2 w-2 shrink-0 rounded-full ${TONE[tone].dot}`} />}
        right={d.mancanti > 0 && structured(d) ? <span className="text-[11px] text-amber-700">{d.mancanti}</span> : null}
      />
    </div>
  );
}

function TreeRow({ label, active, onClick, icon, right }: { label: string; active: boolean; onClick: () => void; icon: ReactNode; right?: ReactNode }) {
  return (
    <button type="button" onClick={onClick} title={label} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left ${active ? 'bg-accent/10 font-medium text-accent' : 'text-ink/75 hover:bg-black/[0.04]'}`}>
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {right}
    </button>
  );
}

/* ---------- Spostare un documento ---------- */

function MoveDialog({ doc, from, list, onClose }: { doc: { id: string; titolo: string }; from: Voce | null; list: Voce[]; onClose: () => void }) {
  const drive = useDrive();
  const choices = list.filter((d) => d.stato !== 'non_riguarda').sort((a, b) => a.titolo.localeCompare(b.titolo));
  const [voceId, setVoceId] = useState(from?.id ?? choices[0]?.id ?? '');
  const voce = choices.find((d) => d.id === voceId) ?? null;
  const items = voce && structured(voce) ? allItems(voce) : [];
  const current = from && voce?.id === from.id ? (from.file.find((f) => f.id === doc.id) ?? null) : null;
  const [key, setKey] = useState<string>(current ? (keyOf(from!, current) ?? '') : '');
  useEffect(() => {
    if (!current) setKey('');
  }, [voceId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md space-y-4 rounded-2xl bg-white p-5 shadow-xl">
        <div>
          <p className="text-sm font-semibold">{from ? 'Sposta in un’altra cartella' : 'Metti in una cartella'}</p>
          <p className="mt-0.5 flex items-center gap-2 truncate text-xs text-ink/55">
            <FileIcon name={doc.titolo} /> {doc.titolo}
          </p>
        </div>
        <Select label="Cartella" value={voceId} onChange={(e) => setVoceId(e.target.value)}>
          {choices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.titolo}
            </option>
          ))}
        </Select>
        {voce && items.length > 0 && (
          <Select label={voce.raccolta ? 'A chi o cosa si riferisce' : 'Periodo'} value={key} onChange={(e) => setKey(e.target.value)}>
            <option value="">{voce.raccolta ? 'Da assegnare' : 'Lo ricava il sistema dalla lettura'}</option>
            {[...items].reverse().map((p) => (
              <option key={p.chiave} value={p.chiave}>
                {p.etichetta}
                {p.stato === 'mancante' ? ' · manca' : p.stato === 'caricato' ? ' · c’è già' : ''}
              </option>
            ))}
          </Select>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            disabled={!voce}
            onClick={() => {
              drive.moveTo({ voce: voce!, key: key || null, label: '' }, doc.id, from);
              onClose();
            }}
          >
            Sposta
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Pagina ---------- */

const readView = (): 'griglia' | 'elenco' => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'elenco' ? 'elenco' : 'griglia';
  } catch {
    return 'griglia';
  }
};

/** Archivio dell'azienda come un Drive (ADR 0122). `path` = ciò che segue `archivio` nell'indirizzo. */
export function ArchivePage({ path }: { path: string[] }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const requiredKey = ['tenant', tenant.id, 'archive', 'required'];
  const q = useTenantQuery<RequiredDocumentsState>(['archive', 'required'], '/archive/required', { refetchInterval: (s) => (s?.generating ? 3000 : false) });
  const docsQ = useTenantQuery<{ data: Doc[] }>(['archive', 'documents', ''], '/archive/documents');
  const general = useGeneralUpload();
  const [view, setViewState] = useState(readView);
  const [filter, setFilter] = useState('tutte');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [moving, setMoving] = useState<{ doc: { id: string; titolo: string }; from: Voce | null } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const searchBox = useRef<HTMLInputElement>(null);
  const pickTarget = useRef<Target | 'generale' | null>(null);
  const base = `/t/${tenant.id}/archivio`;
  const state = q.data;
  const list = state?.data ?? [];
  const docs = docsQ.data?.data ?? [];
  const linked = useMemo(() => new Set(list.flatMap((d) => d.file.map((f) => f.id))), [list]);
  const unlinked = docs.filter((d) => !linked.has(d.id));

  const setView = (v: 'griglia' | 'elenco') => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* solo una comodità */
    }
  };
  // «/» porta alla ricerca, come nei programmi di file.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === '/' && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement)) {
        e.preventDefault();
        searchBox.current?.focus();
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, []);

  const refresh = (s?: RequiredDocumentsState) => {
    if (s) queryClient.setQueryData(requiredKey, s);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'archive', 'documents'] });
  };
  const run = async (label: string, work: () => Promise<RequiredDocumentsState | void>) => {
    setError('');
    setBusy(label);
    try {
      refresh((await work()) ?? undefined);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const where = (to: Target) => `«${to.voce.titolo}»${to.label ? ` › ${to.label}` : ''}`;
  const param = (to: Target) => (to.key ? `?${subParam(to.voce)}=${encodeURIComponent(to.key)}` : '');

  const drive: Drive = {
    base,
    state,
    docs,
    isManager,
    view,
    busy,
    open: (...p) => {
      setSearch('');
      setQuery('');
      navigate([base, ...p].join('/'));
    },
    uploadTo: (to, files) =>
      void run(`Caricamento in ${where(to)}…`, async () => {
        let s: RequiredDocumentsState | undefined;
        for (const [i, f] of files.entries()) {
          setBusy(`Caricamento in ${where(to)}: ${i + 1} di ${files.length}…`);
          s = await apiUpload<RequiredDocumentsState>(t(`/archive/required/${to.voce.id}/upload${param(to)}`), f);
        }
        return s;
      }),
    pick: (to) => {
      pickTarget.current = to;
      input.current?.click();
    },
    moveTo: (to, docId, from) =>
      void run(`Sposto in ${where(to)}…`, async () => {
        if (from?.id === to.voce.id) return api<RequiredDocumentsState>(t(`/archive/required/${to.voce.id}/files/${docId}/${subPath(to.voce)}`), { body: { [subParam(to.voce)]: to.key } });
        const s = await api<RequiredDocumentsState>(t(`/archive/required/${to.voce.id}/files/${docId}/link`), { body: to.key ? { [subParam(to.voce)]: to.key } : {} });
        return from ? api<RequiredDocumentsState>(t(`/archive/required/${from.id}/files/${docId}/unlink`), { body: {} }) : s;
      }),
    act: (voce, p, body) => void run('Aggiorno…', () => api<RequiredDocumentsState>(t(`/archive/required/${voce.id}/${p}`), { body: body ?? {} })),
    remove: (doc) => {
      if (!confirm(`Eliminare definitivamente «${doc.titolo}»? Se ne va anche ciò che il sistema sapeva solo da questo documento. Se lo ricarichi, viene riletto da capo.`)) return;
      void run('Elimino…', async () => {
        await api(t(`/archive/documents/${doc.id}`), { method: 'DELETE' });
        await queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      });
    },
    download: (doc) => {
      setError('');
      apiDownload(`/tenants/${tenant.id}/archive/documents/${doc.id}/file`, doc.titolo).catch((e: unknown) => setError(`${doc.titolo}: ${errorMessage(e)}`));
    },
    askMove: (doc, from) => setMoving({ doc, from }),
  };

  // Dove siamo: radice, «altri», una voce (con anno, periodo o elemento).
  const [first, ...sub] = path;
  const voce = first && first !== 'altri' ? (list.find((d) => d.id === first) ?? null) : null;
  const leafKey = voce ? (voce.raccolta ? (sub[0] ? decodeURIComponent(sub[0]) : null) : voce.frequenza_mesi === 12 ? (sub[0] ?? null) : (sub[1] ?? null)) : null;
  const leaf = voce && leafKey ? (allItems(voce).find((p) => p.chiave === leafKey) ?? null) : null;
  const here: Target | 'generale' | null = voce ? (voce.stato === 'non_riguarda' ? null : { voce, key: leafKey, label: leaf?.etichetta ?? '' }) : 'generale';

  const crumbs: Array<{ label: string; path: string[] }> = [{ label: 'Archivio', path: [] }];
  if (first === 'altri') {
    crumbs.push({ label: 'Altri documenti', path: ['altri'] });
    if (sub[0]) crumbs.push({ label: sub[0], path: ['altri', sub[0]] });
    if (sub[1]) crumbs.push({ label: MONTHS[Number(sub[1]) - 1] ?? sub[1], path: ['altri', sub[0]!, sub[1]] });
  } else if (voce) {
    crumbs.push({ label: voce.titolo, path: [voce.id] });
    if (voce.raccolta && leafKey) crumbs.push({ label: leaf?.etichetta ?? leafKey, path: [voce.id, sub[0]!] });
    else if (!voce.raccolta && sub[0]) {
      crumbs.push({ label: sub[0], path: [voce.id, sub[0]] });
      if (sub[1] && voce.frequenza_mesi !== 12) crumbs.push({ label: leaf?.etichetta ?? sub[1], path: [voce.id, sub[0], sub[1]] });
    }
  }

  // Trascinare file nella pagina li carica nella cartella aperta (o nel caricamento generale, alla radice).
  const pageDrop = useDropTarget(
    isManager && here ? (files) => (here === 'generale' ? general.take(files) : drive.uploadTo(here, files)) : null,
    isManager && here && here !== 'generale' ? (id, from) => drive.moveTo(here, id, voceById(drive, from)) : null,
  );
  const status = busy ?? general.label;

  let content: ReactNode;
  if (search) content = <SearchView q={search} list={list} />;
  else if (first === 'altri') content = <OthersView docs={unlinked} sub={sub} />;
  else if (first && !voce) content = q.isPending ? <p className="text-sm text-ink/50">Carico…</p> : <p className="text-sm text-ink/50">Questa cartella non c’è più. <button className="text-accent hover:underline" onClick={() => drive.open()}>Torna all’archivio</button></p>;
  else if (voce) content = <VoceView d={voce} sub={sub} />;
  else if (state?.needs_profile)
    content = (
      <div className="space-y-6">
        <p className="rounded-xl border border-black/[0.07] bg-white p-4 text-sm text-ink/65">
          Per preparare le cartelle dei documenti obbligatori il sistema deve prima conoscere l’azienda.{' '}
          {isManager && (
            <button className="text-accent underline" onClick={() => navigate(`/t/${tenant.id}/configurazione`)}>
              Vai alla configurazione
            </button>
          )}
        </p>
        <RootView list={[]} filter={filter} setFilter={setFilter} docs={docs} unlinked={unlinked} />
      </div>
    );
  else
    content = (
      <>
        {!list.length && (
          <p className="mb-6 rounded-xl border border-black/[0.07] bg-white p-4 text-sm text-ink/65">
            {state?.generating ? 'Sto ricavando quali documenti servono alla vostra azienda…' : q.isPending ? 'Carico…' : isManager ? 'Nessuna cartella ancora: usa «Aggiorna l’elenco» a destra.' : 'Nessuna cartella ancora.'}
          </p>
        )}
        <RootView list={list} filter={filter} setFilter={setFilter} docs={docs} unlinked={unlinked} />
      </>
    );

  return (
    <DriveCtx.Provider value={drive}>
      <PageTitle title="Archivio" subtitle="Le cartelle dei documenti dell’azienda: cosa c’è, cosa manca e per quale periodo. Le righe importate dai file sono in Dati › Tutti i dati salvati." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[14rem_minmax(0,1fr)] 2xl:grid-cols-[15rem_minmax(0,1fr)_19rem]">
        <aside className="hidden lg:block">
          <div className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto pr-1">
            <Tree list={list} current={first} />
          </div>
        </aside>

        <div className="min-w-0 space-y-4" {...pageDrop.props}>
          {/* Barra: percorso, ricerca, vista, caricamento. */}
          <div className="flex flex-wrap items-center gap-2">
            <nav className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm" aria-label="Percorso">
              {(search ? [crumbs[0]!, { label: `Risultati per «${search}»`, path: null as unknown as string[] }] : crumbs).map((c, i, all) => (
                <span key={i} className="flex min-w-0 items-center gap-1">
                  {i > 0 && <span className="text-ink/30">›</span>}
                  {i === all.length - 1 || !c.path ? (
                    <span className="truncate font-semibold">{c.label}</span>
                  ) : (
                    <button className="truncate rounded px-1 text-ink/60 hover:bg-black/5 hover:text-ink" onClick={() => drive.open(...c.path)}>
                      {c.label}
                    </button>
                  )}
                </span>
              ))}
            </nav>
            <form
              className="flex min-w-[12rem] flex-1 sm:max-w-xs"
              onSubmit={(e) => {
                e.preventDefault();
                setSearch(query.trim());
              }}
            >
              <input ref={searchBox} type="search" className="w-full rounded-[10px] border border-black/[0.08] bg-white px-3 py-1.5 text-sm outline-none focus:border-accent/50 focus:ring-2 focus:ring-accent/15" placeholder="Cerca nell’archivio  ( / )" value={query} onChange={(e) => (setQuery(e.target.value), !e.target.value && setSearch(''))} />
            </form>
            <div className="flex rounded-[10px] border border-black/[0.08] bg-white p-0.5" role="group" aria-label="Vista">
              {(['griglia', 'elenco'] as const).map((v) => (
                <button key={v} onClick={() => setView(v)} title={v === 'griglia' ? 'Griglia' : 'Elenco'} className={`rounded-lg px-2 py-1 ${view === v ? 'bg-accent/10 text-accent' : 'text-ink/45 hover:text-ink'}`}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    {v === 'griglia' ? <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
                  </svg>
                </button>
              ))}
            </div>
            {isManager && here && (
              <Button className="!py-1.5" disabled={!!status} onClick={() => (here === 'generale' ? ((pickTarget.current = 'generale'), input.current?.click()) : drive.pick(here))}>
                + Carica
              </Button>
            )}
          </div>

          <div className="2xl:hidden">{voce && <Details d={voce} leaf={leaf} />}</div>

          {status && <p className="rounded-lg bg-accent/[0.07] px-3 py-2 text-sm text-accent">{status}</p>}
          <ErrorText>{error || (q.error ? errorMessage(q.error) : '')}</ErrorText>
          {here === 'generale' && general.panel}

          <div className={`min-h-[16rem] rounded-2xl transition ${pageDrop.over ? 'bg-accent/[0.04] outline-dashed outline-2 outline-offset-8 outline-accent/50' : ''}`}>{content}</div>
        </div>

        <aside className="hidden 2xl:block">
          <div className="sticky top-4 space-y-4">{voce ? <Details d={voce} leaf={leaf} /> : <RootDetails />}</div>
        </aside>
      </div>

      {!voce && (
        <div className="mt-6 2xl:hidden">
          <RootDetails />
        </div>
      )}
      {moving && <MoveDialog doc={moving.doc} from={moving.from} list={list} onClose={() => setMoving(null)} />}
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          const to = pickTarget.current;
          if (!files.length || !to) return;
          if (to === 'generale') general.take(files);
          else drive.uploadTo(to, files);
        }}
      />
    </DriveCtx.Provider>
  );
}
